import { Box, Card, CardActions, CardContent, Link, Typography } from '@mui/material';
import { t } from 'i18next';
import { MouseEvent, useState } from 'react';
import { Details as DetailsModel } from '../models/valueObjects/Details';

type DetailSectionProps = {
  title: string;
  description: string;
};

/**
 * Helper component for easier wrapping of a single box.
 */
const DetailSection = ({ title, description }: DetailSectionProps) => {
  return (
    <Box sx={{ pb: 1 }}>
      <Typography variant="subtitle1" component="p" sx={{ fontSize: '0.875rem', fontWeight: 'bold', color: 'text.primary' }}>
        {title}
      </Typography>
      <Typography variant="body1" component="p" sx={{ fontSize: '0.875rem' }}>
        {description}
      </Typography>
    </Box>
  );
};

type Props = {
  model: DetailsModel;
  alwaysOpen?: boolean;
};

/**
 * Collapsable details card for Issue details.
 */
export const Details = ({ model, alwaysOpen = false }: Props) => {
  const [isExpanded, setExpanded] = useState(alwaysOpen);

  const toggleExpanded = (e: MouseEvent) => {
    // We are handling link click, so we have to prevent it from default behaviour.
    e.preventDefault();
    e.stopPropagation();

    setExpanded((prevExpanded) => !prevExpanded);
  };

  return (
    <Card elevation={0} sx={{ backgroundColor: 'action.hover' }}>
      <CardContent>
        <DetailSection title={t('consequences')} description={model.getConsequences()} />
        {isExpanded && (
          <>
            {model.getExplanation() && <DetailSection title={t('explanation')} description={model.getExplanation()} />}
            {model.getExpertise() && <DetailSection title={t('expertiseRequired')} description={model.getExpertise()} />}
            {model.getAccess() && <DetailSection title={t('accessRequired')} description={model.getAccess()} />}
            {model.getResources() && <DetailSection title={t('resourcesRequired')} description={model.getResources()} />}

            {model.getReferences().length > 0 && (
              <>
                <Typography variant="subtitle1" component="p" sx={{ fontSize: '0.875rem', fontWeight: 'bold', color: 'text.primary' }}>
                  {t('references')}
                </Typography>
                <ul style={{ marginTop: 0 }}>
                  {model.getReferences().map((reference, idx) => {
                    const href = reference.getHref();
                    return (
                      <li key={idx}>
                        {href ? (
                          <Link target="_blank" href={href} variant="body2">
                            {reference.getText()}
                          </Link>
                        ) : (
                          reference.getText()
                        )}
                      </li>
                    );
                  })}
                </ul>
              </>
            )}
          </>
        )}
      </CardContent>
      {!alwaysOpen && (
        <CardActions sx={{ p: 2, pt: 1 }}>
          <Link component="button" sx={{ cursor: 'pointer' }} onClick={(e) => toggleExpanded(e)}>
            {isExpanded ? t('collapseInformation') : t('expandInformation')}
          </Link>
        </CardActions>
      )}
    </Card>
  );
};
